import {
	type Cart,
	type CheckoutCart,
	DELIVERY_METHOD_KIND,
} from "@dransay/api/models";

export const FULFILLMENT_GROUP_KIND = {
	MEDICATION: "medication",
	CROSS_SELLING: "cross_selling",
} as const;

export type FulfillmentGroupKind =
	(typeof FULFILLMENT_GROUP_KIND)[keyof typeof FULFILLMENT_GROUP_KIND];

/** One vendor's slice of the basket — the unit the delivery step confirms. */
export interface FulfillmentGroup {
	id: string;
	kind: FulfillmentGroupKind;
	vendor: Vendor;
	deliveryMethodUuid: string | null;
	deliverySelectionStale: boolean;
	itemIds: string[];
	itemsPrice: number;
	shippingPrice: number | null;
}

type Vendor = NonNullable<Cart["vendor"]>;

const KIND_ORDER: Record<FulfillmentGroupKind, number> = {
	[FULFILLMENT_GROUP_KIND.MEDICATION]: 0,
	[FULFILLMENT_GROUP_KIND.CROSS_SELLING]: 1,
};

/**
 * The basket's packages, ordered for display.
 *
 * The server owns the partition and the per-group prices, and always answers
 * with at least one group for a non-empty basket — a cart with no group rows
 * still yields a synthesized one, so an empty answer never means a cart that
 * predates groups.
 *
 * Packages with no lines are dropped here, so an empty list means an empty
 * basket, an unresolvable vendor, or a cart whose rows exist but hold none of
 * its items — the last only reachable if a line is left unassigned to a group.
 *
 * Deliberately the only place that knows how groups are read off the cart.
 */
/**
 * The packages the summary actually composes, or none.
 *
 * Composition is gated twice — behind `enableOtcPurchase`, and on there being
 * more than one package — because a basket that fails either keeps exactly the
 * single-vendor rows it had before OTC (FR-030). Stated once here so that
 * everything reading those composed packages agrees on when they exist: the
 * settlement rows the summary draws, and the copy that has to describe the same
 * charge (FR-047). Two copies of this rule would let the separate-payment option
 * promise accessories beside a prescription-only total.
 */
export function resolveComposedPackages({
	cart,
	enableOtcPurchase,
}: {
	cart: CheckoutCart | null | undefined;
	enableOtcPurchase: boolean;
}): FulfillmentGroup[] {
	if (!cart || !enableOtcPurchase) {
		return [];
	}

	const groups = resolveFulfillmentGroups(cart);

	return groups.length > 1 ? groups : [];
}

export function resolveFulfillmentGroups(
	cart: CheckoutCart,
): FulfillmentGroup[] {
	return orderFulfillmentGroups(
		cart.fulfillmentGroups
			// A vendor whose last basket line is gone has no package to ship
			// (FR-014). Removing the accessories package takes its group row with
			// it (OTC-95), but emptying that package one line at a time from the
			// basket instead leaves the row behind, so an empty one is still
			// dropped here rather than drawn as a package with nothing in it.
			.filter((group) => group.itemIds.length > 0)
			.map((group) => ({
				...group,
				// The cart leaves `contact` optional; the vendor card wants it present.
				vendor: { ...group.vendor, contact: group.vendor.contact ?? null },
			})),
	);
}

/**
 * Presents a package's only delivery method as its current selection, so the
 * customer confirms rather than choosing from a list of one (FR-032). A package
 * whose vendor offers a choice is left with none, which is what keeps the
 * confirm control blocked until the customer makes it (FR-006).
 *
 * Composed by the delivery step rather than folded into
 * `resolveFulfillmentGroups`: the summary's per-vendor recap and its cost rows
 * read the same groups, and both must state the method the customer chose and
 * the cart holds (FR-009), never one derived on their behalf.
 *
 * Nothing is written here. The cart learns the method when the package is
 * confirmed, through the same vendor-keyed write a card click produces.
 */
export function preselectSoleDeliveryMethod(
	groups: readonly FulfillmentGroup[],
): FulfillmentGroup[] {
	return groups.map((group) => {
		if (group.deliveryMethodUuid) {
			return group;
		}

		const sole = soleEnabledMethodUuid(group.vendor);

		return sole ? { ...group, deliveryMethodUuid: sole } : group;
	});
}

/**
 * The vendor's method when it is the only one it offers.
 *
 * Express courier never qualifies: its card is shown only once a postal-code
 * availability check passes, and `DeliveryMethodCards` deselects a disabled
 * one, so presenting it as chosen here would contend with that (FR-013).
 */
function soleEnabledMethodUuid(vendor: Vendor): string | null {
	const enabled = vendor.deliveryMethods.filter((method) => method.enabled);

	if (enabled.length !== 1) {
		return null;
	}

	const [method] = enabled;

	return method.kind === DELIVERY_METHOD_KIND.EXPRESS_COURIER
		? null
		: method.uuid;
}

/** Medication package first, matching the `PACKAGE n OF m` order in the design. */
export function orderFulfillmentGroups(
	groups: readonly FulfillmentGroup[],
): FulfillmentGroup[] {
	return [...groups].sort((a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind]);
}

export function isDeliveryStepReachable(options: {
	cart: CheckoutCart | null | undefined;
	enableOtcPurchase: boolean;
}): boolean {
	return resolveComposedPackages(options).length > 0;
}

/**
 * Whether the basket holds an accessories package.
 *
 * Distinct from isDeliveryStepReachable, which asks whether the delivery step
 * has more than one package to confirm. This asks only whether accessories are
 * present, which is what flips the cross-sell CTA from "skip and continue" to
 * "continue" and so which of that screen's two step titles applies.
 */
export function hasCrossSellingGroup(cart: CheckoutCart): boolean {
	return resolveFulfillmentGroups(cart).some(
		(group) => group.kind === FULFILLMENT_GROUP_KIND.CROSS_SELLING,
	);
}
